import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
	FiArrowLeft,
	FiDownload,
	FiPlus,
	FiCamera,
	FiType,
	FiImage,
	FiStar,
	FiTrash2,
	FiX,
} from "react-icons/fi";
import logoCuadrado from "../../assets/logo-cuadrado.png";
import ScrapbookCanvas from "./ScrapbookCanvas";
import {
	CANVAS_W,
	CANVAS_H,
	BACKGROUNDS,
	COVER_TEMPLATES,
	PAGE_TEMPLATES,
	ALL_TEMPLATES,
	DECO_STICKERS,
	WASHI_TAPES,
	CITY_STICKERS,
	COVER_CATEGORIES,
	FONT_OPTIONS,
	TEXT_COLOR_PRESETS,
	DEFAULT_PAGE_BG_ID,
} from "../../data/scrapbook/config";
import "./ScrapbookEditor.css";

let counter = 0;
function uid(prefix = "sb") {
	return `${prefix}-${Date.now()}-${++counter}`;
}

function isMobileDevice() {
	if (typeof window === "undefined") return false;
	return (
		window.matchMedia("(max-width: 768px)").matches ||
		/iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "")
	);
}

function dataUrlToFile(dataUrl, filename) {
	const comma = dataUrl.indexOf(",");
	const header = dataUrl.slice(0, Math.max(comma, 0));
	const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
	const mime = /:(.*?);/.exec(header)?.[1] || "image/png";
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return new File([bytes], filename, { type: mime });
}

function canShareImageFile(file) {
	try {
		return typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
	} catch {
		return false;
	}
}

function triggerAnchorDownload(href, filename) {
	const link = document.createElement("a");
	link.download = filename;
	link.href = href;
	link.rel = "noopener";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

const DEFAULT_TEXT_COLOR = "#3d2c1e";

function textsFromSlots(slots) {
	return (slots || []).map((slot) => ({
		id: uid("txt"),
		slotId: slot.id,
		value: slot.defaultValue || "",
		placeholder: slot.placeholder || "Escribe aquí...",
		x: slot.x,
		y: slot.y,
		width: slot.width || 800,
		fontSize: slot.fontSize || 36,
		fontFamily: slot.fontFamily || "Playfair Display",
		fontStyle: slot.fontStyle || "normal",
		align: slot.align || "center",
		color: slot.color || DEFAULT_TEXT_COLOR,
		rotation: 0,
		scaleX: 1,
		scaleY: 1,
	}));
}

function photosFromSlots(slots) {
	return (slots || []).map((slot) => ({
		id: uid("photo"),
		slotId: slot.id,
		src: null,
		x: slot.x,
		y: slot.y,
		width: slot.width,
		height: slot.height,
		rotation: slot.rotation || 0,
		frame: slot.frame || "polaroid",
		tapeAngle: Math.random() > 0.5 ? 3 : -3,
		scaleX: 1,
		scaleY: 1,
	}));
}

const TABS = [
	{ id: "templates", icon: <FiImage />, label: "Plantilla" },
	{ id: "photos", icon: <FiCamera />, label: "Fotos" },
	{ id: "texts", icon: <FiType />, label: "Textos" },
	{ id: "stickers", icon: <FiStar />, label: "Stickers" },
	{ id: "download", icon: <FiDownload />, label: "Descargar" },
];

export default function ScrapbookEditor() {
	const navigate = useNavigate();
	const location = useLocation();
	const handleBack = () => {
		const from = location.state?.from;
		if (typeof from === "string" && from.startsWith("/") && from !== "/album") {
			navigate(from);
			return;
		}
		if (typeof window !== "undefined" && window.history.length > 1) {
			navigate(-1);
			return;
		}
		navigate("/");
	};
	const canvasRef = useRef(null);
	const fileInputRef = useRef(null);
	const uploadModeRef = useRef(null); // { type: 'slot', slotId } | { type: 'free' }

	const [activeTab, setActiveTab] = useState("templates");
	const [panelOpen, setPanelOpen] = useState(false);
	const [pageCategory, setPageCategory] = useState("covers"); // "covers" | "pages"
	const [templateId, setTemplateId] = useState(COVER_TEMPLATES[0].id);
	const [bgId, setBgId] = useState(BACKGROUNDS[0].id);
	const [coverId, setCoverId] = useState(null);
	const [photos, setPhotos] = useState(() => photosFromSlots(COVER_TEMPLATES[0].photoSlots));
	const [texts, setTexts] = useState(() => textsFromSlots(COVER_TEMPLATES[0].textSlots));
	const [stickers, setStickers] = useState([]);
	const [stickerPack, setStickerPack] = useState("decorativos");
	const [focusedTextId, setFocusedTextId] = useState(null);
	const [focusedPhotoId, setFocusedPhotoId] = useState(null);
	const [exporting, setExporting] = useState(false);
	const [exportStatus, setExportStatus] = useState(null);
	const [selectId, setSelectId] = useState(null);
	const textFieldRefs = useRef({});
	const photoFieldRefs = useRef({});

	/* ── Multi-page state ── */
	const [pages, setPages] = useState(() => [{
		id: uid("page"),
		templateId: COVER_TEMPLATES[0].id,
		bgId: BACKGROUNDS[0].id,
		coverId: null,
		photos: photosFromSlots(COVER_TEMPLATES[0].photoSlots),
		texts: textsFromSlots(COVER_TEMPLATES[0].textSlots),
		stickers: [],
		pageCategory: "covers",
	}]);
	const [activePageIdx, setActivePageIdx] = useState(0);

	const template = ALL_TEMPLATES.find((t) => t.id === templateId) || COVER_TEMPLATES[0];
	const background = BACKGROUNDS.find((b) => b.id === bgId) || BACKGROUNDS[0];

	/* Load decorative fonts so Konva can render & export them */
	useEffect(() => {
		if (!document.fonts) return;
		FONT_OPTIONS.forEach((font) => {
			document.fonts.load(`700 48px "${font.id}"`).catch(() => {});
		});
		document.fonts.load(`600 22px "Poppins"`).catch(() => {});
	}, []);

	/* Compute which template slots are still empty */
	const emptyPhotos = useMemo(
		() => photos.filter((p) => p.slotId && !p.src),
		[photos]
	);

	/* ── Template change ── */
	const changeTemplate = (id) => {
		const next = ALL_TEMPLATES.find((t) => t.id === id) || COVER_TEMPLATES[0];
		photos.forEach((p) => { if (p.src) URL.revokeObjectURL(p.src); });
		setTemplateId(id);
		setCoverId(null);
		setPhotos(photosFromSlots(next.photoSlots));
		setTexts(textsFromSlots(next.textSlots));
		setStickers([]);
	};

	/* ── Open file picker ── */
	const openFilePicker = useCallback((mode) => {
		uploadModeRef.current = mode;
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
			fileInputRef.current.click();
		}
	}, []);

	const handlePhotoUpload = useCallback((photoId) => {
		openFilePicker({ type: "photo", photoId });
	}, [openFilePicker]);

	const handleFreePhotoUpload = useCallback(() => {
		openFilePicker({ type: "free" });
	}, [openFilePicker]);

	/* ── Handle file selection ── */
	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		const mode = uploadModeRef.current;
		if (!file || !mode) return;

		const url = URL.createObjectURL(file);

		if (mode.type === "photo") {
			setPhotos((prev) =>
				prev.map((p) => {
					if (p.id !== mode.photoId) return p;
					if (p.src) URL.revokeObjectURL(p.src);
					return { ...p, src: url };
				})
			);
		} else if (mode.type === "slot") {
			setPhotos((prev) => {
				const existing = prev.find((p) => p.slotId === mode.slotId && !p.src);
				if (existing) {
					return prev.map((p) =>
						p.id === existing.id ? { ...p, src: url } : p
					);
				}
				const slot = template.photoSlots.find((s) => s.id === mode.slotId);
				if (!slot) return prev;
				return [
					...prev,
					{
						id: uid("photo"),
						slotId: slot.id,
						src: url,
						x: slot.x,
						y: slot.y,
						width: slot.width,
						height: slot.height,
						rotation: slot.rotation || 0,
						frame: slot.frame || "polaroid",
						tapeAngle: Math.random() > 0.5 ? 3 : -3,
						scaleX: 1,
						scaleY: 1,
					},
				];
			});
		} else {
			setPhotos((prev) => [
				...prev,
				{
					id: uid("photo"),
					slotId: null,
					src: url,
					x: CANVAS_W / 2 + (Math.random() - 0.5) * 200,
					y: CANVAS_H / 2 + (Math.random() - 0.5) * 300,
					width: 440,
					height: 440,
					rotation: (Math.random() - 0.5) * 10,
					frame: "polaroid",
					tapeAngle: Math.random() > 0.5 ? 3 : -3,
					scaleX: 1,
					scaleY: 1,
				},
			]);
		}
		uploadModeRef.current = null;
	};

	/* ── Photo drag, transform & remove ── */
	const handlePhotoDragEnd = (id, x, y) => {
		setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, x, y } : p)));
	};

	const handlePhotoTransform = (id, attrs) => {
		setPhotos((prev) =>
			prev.map((p) =>
				p.id === id
					? { ...p, x: attrs.x, y: attrs.y, rotation: attrs.rotation, scaleX: attrs.scaleX, scaleY: attrs.scaleY }
					: p
			)
		);
	};

	const removePhoto = (id) => {
		setPhotos((prev) => {
			const photo = prev.find((p) => p.id === id);
			if (photo?.src) URL.revokeObjectURL(photo.src);
			return prev.filter((p) => p.id !== id);
		});
	};

	const clearPhotoSrc = (id) => {
		setPhotos((prev) =>
			prev.map((p) => {
				if (p.id !== id) return p;
				if (p.src) URL.revokeObjectURL(p.src);
				return { ...p, src: null };
			})
		);
	};

	/* ── Text editing ── */
	const updateText = (id, value) => {
		setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, value } : t)));
	};

	const updateTextFont = (id, fontFamily) => {
		setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, fontFamily } : t)));
	};

	const updateTextColor = (id, color) => {
		setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, color } : t)));
	};

	const updateTextSize = (id, fontSize) => {
		const size = Math.max(18, Math.min(200, Math.round(fontSize)));
		setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, fontSize: size, scaleX: 1, scaleY: 1 } : t)));
	};

	const handleTextDragEnd = (id, x, y) => {
		setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, x, y } : t)));
	};

	const handleTextTransform = (id, attrs) => {
		setTexts((prev) =>
			prev.map((t) => {
				if (t.id !== id) return t;
				const avgScale = (Math.abs(attrs.scaleX || 1) + Math.abs(attrs.scaleY || 1)) / 2;
				const nextSize = Math.max(18, Math.min(200, Math.round((t.fontSize || 36) * avgScale)));
				return {
					...t,
					x: attrs.x,
					y: attrs.y,
					rotation: attrs.rotation,
					fontSize: nextSize,
					scaleX: 1,
					scaleY: 1,
				};
			})
		);
	};

	const removeText = (id) => {
		setTexts((prev) => prev.filter((t) => t.id !== id));
	};

	const addText = () => {
		setTexts((prev) => [
			...prev,
			{
				id: uid("txt"),
				slotId: null,
				value: "",
				placeholder: "Escribe aquí...",
				x: CANVAS_W / 2,
				y: CANVAS_H / 2 + (Math.random() - 0.5) * 200,
				width: 700,
				fontSize: 42,
				fontFamily: "Playfair Display",
				fontStyle: "bold",
				align: "center",
				color: DEFAULT_TEXT_COLOR,
				rotation: 0,
				scaleX: 1,
				scaleY: 1,
			},
		]);
	};

	const handleTextSelectFromCanvas = useCallback((id) => {
		setFocusedTextId(id);
		setActiveTab("texts");
		setPanelOpen(true);
	}, []);

	const handlePhotoSelectFromCanvas = useCallback((id) => {
		setFocusedPhotoId(id);
		setActiveTab("photos");
		setPanelOpen(true);
	}, []);

	const handleTabClick = (tabId) => {
		if (panelOpen && activeTab === tabId) {
			setPanelOpen(false);
			return;
		}
		setActiveTab(tabId);
		setPanelOpen(true);
	};

	const closePanel = () => setPanelOpen(false);

	useEffect(() => {
		if (activeTab !== "texts" || !focusedTextId) return;
		const el = textFieldRefs.current[focusedTextId];
		if (!el) return;
		el.scrollIntoView({ behavior: "smooth", block: "nearest" });
		const input = el.querySelector("input, textarea");
		if (input) {
			setTimeout(() => input.focus(), 120);
		}
	}, [activeTab, focusedTextId]);

	useEffect(() => {
		if (activeTab !== "photos" || !focusedPhotoId) return;
		const el = photoFieldRefs.current[focusedPhotoId];
		if (!el) return;
		el.scrollIntoView({ behavior: "smooth", block: "nearest" });
	}, [activeTab, focusedPhotoId]);

	/* ── Stickers ── */
	const placeSticker = (sticker) => {
		setStickers((prev) => [...prev, sticker]);
		setSelectId(sticker.id);
		closePanel();
	};

	const addDecoSticker = (deco) => {
		placeSticker({
			id: uid("stk"),
			type: "image",
			imageSrc: deco.image,
			x: CANVAS_W / 2 + (Math.random() - 0.5) * 200,
			y: CANVAS_H / 2 + (Math.random() - 0.5) * 300,
			rotation: (Math.random() - 0.5) * 12,
			scaleX: 1,
			scaleY: 1,
		});
	};

	const addWashiTape = (tape) => {
		placeSticker({
			id: uid("stk"),
			type: "washi",
			content: "",
			color: tape.color || null,
			imageSrc: tape.image || null,
			width: tape.width || 320,
			height: tape.height || 44,
			x: CANVAS_W / 2,
			y: CANVAS_H / 2 + (Math.random() - 0.5) * 400,
			rotation: (Math.random() - 0.5) * 8,
			scaleX: 1,
			scaleY: 1,
		});
	};

	const addCitySticker = (sticker) => {
		placeSticker({
			id: uid("stk"),
			type: "image",
			imageSrc: sticker.image,
			x: CANVAS_W / 2 + (Math.random() - 0.5) * 200,
			y: CANVAS_H / 2 + (Math.random() - 0.5) * 300,
			rotation: (Math.random() - 0.5) * 12,
		});
	};

	const handleStickerDragEnd = (id, x, y) => {
		setStickers((prev) => prev.map((s) => (s.id === id ? { ...s, x, y } : s)));
	};

	const handleStickerTransform = (id, attrs) => {
		setStickers((prev) =>
			prev.map((s) =>
				s.id === id
					? { ...s, x: attrs.x, y: attrs.y, rotation: attrs.rotation, scaleX: attrs.scaleX, scaleY: attrs.scaleY }
					: s
			)
		);
	};

	const removeSticker = (id) => {
		setStickers((prev) => prev.filter((s) => s.id !== id));
	};

	/* ── Export ── */
	const downloadPNG = async () => {
		if (exporting) return;
		const stage = canvasRef.current?.getStage();
		if (!stage) return;
		setExporting(true);
		setExportStatus(null);
		const filename = `album-viajes-molones-${activePageIdx + 1}.png`;
		try {
			const pixelRatio = CANVAS_W / (stage.width() || 400);
			const uri = stage.toDataURL({ pixelRatio, mimeType: "image/png" });
			const file = dataUrlToFile(uri, filename);
			const useShare = isMobileDevice() && canShareImageFile(file);

			if (useShare) {
				try {
					await navigator.share({ files: [file], title: "Álbum de viaje" });
					setExportStatus("ok");
				} catch (err) {
					if (err?.name === "AbortError") {
						setExportStatus(null);
					} else {
						triggerAnchorDownload(uri, filename);
						setExportStatus("ok");
					}
				}
			} else if (isMobileDevice()) {
				const blobUrl = URL.createObjectURL(file);
				const opened = window.open(blobUrl, "_blank", "noopener");
				if (!opened) triggerAnchorDownload(uri, filename);
				window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
				setExportStatus("ok");
			} else {
				triggerAnchorDownload(uri, filename);
				setExportStatus("ok");
			}
		} catch (err) {
			console.error("Export error:", err);
			setExportStatus("error");
		} finally {
			setExporting(false);
			window.setTimeout(() => setExportStatus(null), 5000);
		}
	};

	/* ── Multi-page helpers ── */
	const captureCurrentPage = () => ({
		templateId, bgId, coverId, photos, texts, stickers, pageCategory,
	});

	const loadPage = (page) => {
		setTemplateId(page.templateId);
		setBgId(page.bgId);
		setCoverId(page.coverId);
		setPhotos(page.photos);
		setTexts(page.texts);
		setStickers(page.stickers);
		setPageCategory(page.pageCategory);
		setFocusedTextId(null);
		setFocusedPhotoId(null);
	};

	const switchToPage = (idx) => {
		if (idx === activePageIdx) return;
		const snap = captureCurrentPage();
		setPages((prev) => prev.map((p, i) => (i === activePageIdx ? { ...p, ...snap } : p)));
		loadPage(pages[idx]);
		setActivePageIdx(idx);
		setActiveTab("templates");
	};

	const addNewPage = () => {
		const snap = captureCurrentPage();
		const firstPage = PAGE_TEMPLATES[0];
		const newPage = {
			id: uid("page"),
			templateId: firstPage.id,
			bgId: firstPage.defaultBgId || DEFAULT_PAGE_BG_ID,
			coverId: null,
			photos: photosFromSlots(firstPage.photoSlots),
			texts: textsFromSlots(firstPage.textSlots),
			stickers: [],
			pageCategory: "pages",
		};
		const newIdx = pages.length;
		setPages((prev) => [
			...prev.map((p, i) => (i === activePageIdx ? { ...p, ...snap } : p)),
			newPage,
		]);
		loadPage(newPage);
		setActivePageIdx(newIdx);
		setActiveTab("templates");
	};

	const deletePage = (idx) => {
		if (pages.length <= 1) return;
		setPages((prev) => prev.filter((_, i) => i !== idx));
		if (idx === activePageIdx) {
			const nextIdx = idx > 0 ? idx - 1 : 0;
			const target = pages[nextIdx === idx ? idx + 1 : nextIdx];
			loadPage(target);
			setActivePageIdx(Math.min(nextIdx, pages.length - 2));
		} else if (idx < activePageIdx) {
			setActivePageIdx((prev) => prev - 1);
		}
	};

	/* ═══════════════════════════════════════════════
	   Render panel tabs
	   ═══════════════════════════════════════════════ */
	/* ── Switch between cover / page category ── */
	const switchCategory = (cat) => {
		setPageCategory(cat);
		const firstTemplate = cat === "covers" ? COVER_TEMPLATES[0] : PAGE_TEMPLATES[0];
		photos.forEach((p) => { if (p.src) URL.revokeObjectURL(p.src); });
		setTemplateId(firstTemplate.id);
		setCoverId(null);
		if (cat === "pages") setBgId(firstTemplate.defaultBgId || DEFAULT_PAGE_BG_ID);
		setPhotos(photosFromSlots(firstTemplate.photoSlots));
		setTexts(textsFromSlots(firstTemplate.textSlots));
		setStickers([]);
	};

	const selectTemplate = (id) => {
		const next = ALL_TEMPLATES.find((t) => t.id === id) || COVER_TEMPLATES[0];
		const isPageTemplate = PAGE_TEMPLATES.some((t) => t.id === id);
		photos.forEach((p) => { if (p.src) URL.revokeObjectURL(p.src); });
		setTemplateId(id);
		if (isPageTemplate) setBgId(next.defaultBgId || DEFAULT_PAGE_BG_ID);
		setPhotos(photosFromSlots(next.photoSlots));
		setTexts(textsFromSlots(next.textSlots));
		setStickers([]);
	};

	const renderTemplatesTab = () => {
		const isCoverMode = pageCategory === "covers";
		const currentTemplates = isCoverMode ? COVER_TEMPLATES : PAGE_TEMPLATES;

		return (
			<div className="sb-tab-content">
				{/* Category toggle */}
				<div className="sb-category-toggle">
					<button
						className={`sb-category-btn ${pageCategory === "covers" ? "sb-category-btn--active" : ""}`}
						onClick={() => switchCategory("covers")}
					>
						Portadas
					</button>
					<button
						className={`sb-category-btn ${pageCategory === "pages" ? "sb-category-btn--active" : ""}`}
						onClick={() => switchCategory("pages")}
					>
						Páginas
					</button>
				</div>

				{/* Template picker */}
				<p className="sb-section-label">
					{isCoverMode ? "Estilo de portada" : "Plantilla de página"}
				</p>
				<div className="sb-template-grid">
					{currentTemplates.map((t) => (
						<button
							key={t.id}
							className={`sb-template-card ${templateId === t.id ? "sb-template-card--active" : ""}`}
							onClick={() => selectTemplate(t.id)}
						>
							<span className="sb-template-card__name">{t.name}</span>
						</button>
					))}
				</div>

				<p className="sb-section-label">Fondo</p>
				<div className="sb-bg-grid">
					{BACKGROUNDS.map((b) => (
						<button
							key={b.id}
							className={`sb-bg-swatch ${b.image ? "sb-bg-swatch--image" : ""} ${bgId === b.id ? "sb-bg-swatch--active" : ""}`}
							style={b.image ? undefined : { background: b.color }}
							onClick={() => setBgId(b.id)}
							title={b.name}
						>
							{b.image && <img src={b.image} alt={b.name} />}
						</button>
					))}
				</div>
			</div>
		);
	};

	const renderPhotosTab = () => (
		<div className="sb-tab-content">
			<p className="sb-section-label">Tus fotos</p>
			<p className="sb-hint">Arrastra en el canvas · Sube o quita desde aquí</p>

			<div className="sb-photo-slots">
				{photos.map((p) => (
					<div
						key={p.id}
						ref={(el) => { photoFieldRefs.current[p.id] = el; }}
						className={`sb-photo-slot-row ${focusedPhotoId === p.id ? "sb-photo-slot-row--focused" : ""}`}
					>
						<div
							className={`sb-photo-slot-preview ${p.src ? "" : "sb-photo-slot-preview--empty"}`}
							onClick={() => handlePhotoUpload(p.id)}
						>
							{p.src ? <img src={p.src} alt="" /> : <FiPlus />}
						</div>
						<div className="sb-photo-slot-info">
							<span className="sb-photo-slot-name">
								{p.slotId ? p.slotId.replace(/[-_]/g, " ") : "Foto libre"}
							</span>
							{p.src ? (
								<button
									className="sb-photo-remove"
									onClick={() => clearPhotoSrc(p.id)}
								>
									<FiTrash2 size={14} /> Quitar imagen
								</button>
							) : (
								<button
									className="sb-photo-upload-btn"
									onClick={() => handlePhotoUpload(p.id)}
								>
									<FiCamera size={14} /> Subir foto
								</button>
							)}
						</div>
					</div>
				))}
			</div>

			{emptyPhotos.length === 0 && photos.length === 0 && (
				<p className="sb-hint">Esta plantilla no tiene huecos de foto. Añade una foto libre.</p>
			)}

			<button className="sb-add-free-photo" onClick={handleFreePhotoUpload}>
				<FiPlus size={16} />
				Añadir foto libre
			</button>
		</div>
	);

	const renderTextsTab = () => (
		<div className="sb-tab-content">
			<p className="sb-section-label">Textos</p>
			<p className="sb-hint">Edita el texto, el tamaño, la fuente y el color</p>
			<div className="sb-text-fields">
				{texts.map((item, i) => (
					<div
						key={item.id}
						ref={(el) => { textFieldRefs.current[item.id] = el; }}
						className={`sb-text-field ${focusedTextId === item.id ? "sb-text-field--focused" : ""}`}
					>
						<label className="sb-text-field__label">
							Texto {i + 1}
							<button
								type="button"
								className="sb-photo-remove"
								onClick={() => removeText(item.id)}
								style={{ marginLeft: 8 }}
							>
								<FiTrash2 size={12} /> Quitar
							</button>
						</label>
						{item.placeholder?.length > 80 || item.slotId === "entry" ? (
							<textarea
								className="sb-text-field__textarea"
								value={item.value || ""}
								placeholder={item.placeholder}
								onChange={(e) => updateText(item.id, e.target.value)}
								rows={4}
								style={{ fontFamily: `"${item.fontFamily}", serif`, color: item.color || DEFAULT_TEXT_COLOR }}
							/>
						) : (
							<input
								className="sb-text-field__input"
								type="text"
								value={item.value || ""}
								placeholder={item.placeholder}
								onChange={(e) => updateText(item.id, e.target.value)}
								style={{ fontFamily: `"${item.fontFamily}", serif`, color: item.color || DEFAULT_TEXT_COLOR }}
							/>
						)}
						<p className="sb-text-field__font-label">Tamaño</p>
						<div className="sb-size-row">
							<button
								type="button"
								className="sb-size-btn"
								onClick={() => updateTextSize(item.id, (item.fontSize || 36) - 8)}
							>
								−
							</button>
							<input
								type="range"
								className="sb-size-slider"
								min={18}
								max={200}
								step={2}
								value={item.fontSize || 36}
								onChange={(e) => updateTextSize(item.id, Number(e.target.value))}
							/>
							<button
								type="button"
								className="sb-size-btn"
								onClick={() => updateTextSize(item.id, (item.fontSize || 36) + 8)}
							>
								+
							</button>
							<span className="sb-size-value">{item.fontSize || 36}</span>
						</div>
						<p className="sb-text-field__font-label">Fuente</p>
						<select
							className="sb-font-select"
							value={item.fontFamily || "Playfair Display"}
							onChange={(e) => updateTextFont(item.id, e.target.value)}
							style={{ fontFamily: `"${item.fontFamily || "Playfair Display"}", serif` }}
						>
							{FONT_OPTIONS.map((font) => (
								<option
									key={font.id}
									value={font.id}
									style={{ fontFamily: `"${font.id}", serif` }}
								>
									{font.name}
								</option>
							))}
						</select>
						<p className="sb-text-field__font-label">Color</p>
						<div className="sb-color-row">
							{TEXT_COLOR_PRESETS.map((c) => (
								<button
									key={c}
									type="button"
									className={`sb-color-swatch ${(item.color || DEFAULT_TEXT_COLOR).toLowerCase() === c.toLowerCase() ? "sb-color-swatch--active" : ""}`}
									style={{ background: c }}
									onClick={() => updateTextColor(item.id, c)}
									title={c}
									aria-label={`Color ${c}`}
								/>
							))}
							<label className="sb-color-custom" title="Color personalizado">
								<input
									type="color"
									value={item.color || DEFAULT_TEXT_COLOR}
									onChange={(e) => updateTextColor(item.id, e.target.value)}
								/>
							</label>
						</div>
					</div>
				))}
			</div>
			<button className="sb-add-free-photo" onClick={addText}>
				<FiPlus size={16} />
				Añadir texto
			</button>
		</div>
	);

	const renderStickersTab = () => {
		const packShortName = (id) => {
			const short = {
				decorativos: "Decorativo",
				"nueva-york": "NY",
				japon: "Japón",
				china: "China",
				tokio: "Tokio",
				londres: "Londres",
				paris: "París",
				seul: "Seúl",
			};
			if (short[id]) return short[id];
			return COVER_CATEGORIES.find((c) => c.id === id)?.name || id;
		};
		const cityPackIds = Object.keys(CITY_STICKERS);
		const stickerPackOptions = [
			{ id: "decorativos", name: packShortName("decorativos") },
			...cityPackIds.map((id) => ({ id, name: packShortName(id) })),
		];
		const cityStickers = stickerPack !== "decorativos" ? (CITY_STICKERS[stickerPack] || []) : [];

		return (
			<div className="sb-tab-content">
				<p className="sb-section-label">Stickers</p>
				<p className="sb-hint">Toca uno para verlo en la página · desliza packs</p>

				<div className="sb-sticker-packs" role="tablist" aria-label="Packs de stickers">
					{stickerPackOptions.map((pack) => (
						<button
							key={pack.id}
							type="button"
							role="tab"
							aria-selected={stickerPack === pack.id}
							className={`sb-sticker-pack-chip${stickerPack === pack.id ? " sb-sticker-pack-chip--active" : ""}`}
							onClick={() => setStickerPack(pack.id)}
						>
							{pack.name}
						</button>
					))}
				</div>

				{stickerPack === "decorativos" ? (
					<div className="sb-city-sticker-grid">
						{DECO_STICKERS.map((d) => (
							<button
								key={d.id}
								className="sb-city-sticker-btn"
								onClick={() => addDecoSticker(d)}
								title={d.name}
							>
								<img src={d.image} alt={d.name} />
							</button>
						))}
					</div>
				) : (
					<div className="sb-city-sticker-grid">
						{cityStickers.map((s) => (
							<button
								key={s.id}
								className="sb-city-sticker-btn"
								onClick={() => addCitySticker(s)}
								title={s.name}
							>
								<img src={s.image} alt={s.name} />
							</button>
						))}
					</div>
				)}

				<p className="sb-section-label">Washi tape</p>
				<div className="sb-washi-grid">
					{WASHI_TAPES.map((t) => (
						<button
							key={t.id}
							className={`sb-washi-btn ${t.image ? "sb-washi-btn--image" : ""}`}
							style={t.image ? undefined : { background: t.color }}
							onClick={() => addWashiTape(t)}
							title={t.name}
						>
							{t.image && <img src={t.image} alt={t.name} />}
						</button>
					))}
				</div>
			</div>
		);
	};

	const renderDownloadTab = () => (
		<div className="sb-tab-content sb-download-tab">
			<div className="sb-download-block">
				<button
					className="sb-download-btn"
					onClick={downloadPNG}
					disabled={exporting}
				>
					<FiDownload size={20} />
					<span className="sb-download-btn__desktop">
						{exporting ? "Preparando..." : "Descargar imagen"}
					</span>
					<span className="sb-download-btn__mobile">
						{exporting ? "Preparando..." : "Guardar en fotos"}
					</span>
				</button>
				<p className="sb-download-hint sb-download-hint--desktop">
					PNG 1080×1920 listo para Instagram Stories
				</p>
				<p className="sb-download-hint sb-download-hint--mobile">
					Se abre el menú de tu teléfono. Pulsa <strong>Guardar en Fotos</strong>.
				</p>
				{exportStatus === "ok" && (
					<p className="sb-download-status sb-download-status--ok">Listo</p>
				)}
				{exportStatus === "error" && (
					<p className="sb-download-status sb-download-status--error">
						No se pudo guardar la imagen. Inténtalo de nuevo.
					</p>
				)}
			</div>

			<div className="sb-new-page-block">
				<button
					className="sb-new-page-btn"
					onClick={addNewPage}
				>
					<FiPlus size={18} />
					Crear otra página
				</button>
			</div>

			<div className="sb-cta-block">
				<p className="sb-cta-text">
					¿Quieres saber qué ver y cómo organizar tu próximo viaje?
				</p>
				<button
					className="sb-cta-btn"
					onClick={() => navigate("/")}
				>
					Descubre nuestras guías ✨
				</button>
			</div>
		</div>
	);

	/* ═══════════════════════════════════════════════
	   Main render
	   ═══════════════════════════════════════════════ */
	return (
		<div className="sb-editor">
			<header className="sb-editor__header">
				<button
					className="sb-editor__back"
					onClick={handleBack}
					aria-label="Volver"
				>
					<FiArrowLeft size={20} />
				</button>
				<a href="/" className="sb-editor__brand" aria-label="Viajes Molones">
					<img src={logoCuadrado} alt="Viajes Molones" />
				</a>
				<h1 className="sb-editor__title">Tu álbum de viaje</h1>
				<div className="sb-editor__spacer" />
			</header>

			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				style={{ display: "none" }}
				onChange={handleFileChange}
			/>

			<div className="sb-editor__body">
				{panelOpen && (
					<button
						type="button"
						className="sb-panel-backdrop"
						aria-label="Cerrar menú"
						onClick={closePanel}
					/>
				)}
				<aside className={`sb-panel ${panelOpen ? "sb-panel--open" : ""}`}>
					<nav className="sb-tabs">
						{TABS.map((tab) => (
							<button
								key={tab.id}
								className={`sb-tab ${activeTab === tab.id ? "sb-tab--active" : ""}`}
								onClick={() => handleTabClick(tab.id)}
								title={tab.label}
								aria-expanded={panelOpen && activeTab === tab.id}
							>
								{tab.icon}
								<span className="sb-tab__label">{tab.label}</span>
							</button>
						))}
					</nav>

					<div className="sb-panel__scroll">
						<div className="sb-panel__grabber">
							<span className="sb-panel__grabber-bar" />
							<button
								type="button"
								className="sb-panel__close"
								onClick={closePanel}
								aria-label="Cerrar menú"
							>
								<FiX size={18} />
							</button>
						</div>
						{activeTab === "templates" && renderTemplatesTab()}
						{activeTab === "photos" && renderPhotosTab()}
						{activeTab === "texts" && renderTextsTab()}
						{activeTab === "stickers" && renderStickersTab()}
						{activeTab === "download" && renderDownloadTab()}
					</div>
				</aside>

				<main className="sb-canvas-area">
					<ScrapbookCanvas
						ref={canvasRef}
						template={template}
						background={background}
						coverImage={null}
						photos={photos}
						texts={texts}
						stickers={stickers}
						onPhotoDragEnd={handlePhotoDragEnd}
						onPhotoTransform={handlePhotoTransform}
						onRemovePhoto={removePhoto}
						onPhotoSelect={handlePhotoSelectFromCanvas}
						onTextDragEnd={handleTextDragEnd}
						onTextTransform={handleTextTransform}
						onRemoveText={removeText}
						onTextSelect={handleTextSelectFromCanvas}
						onStickerDragEnd={handleStickerDragEnd}
						onStickerTransform={handleStickerTransform}
						onRemoveSticker={removeSticker}
						selectId={selectId}
					/>

					{/* Page strip */}
					<div className="sb-page-strip">
						{pages.map((page, idx) => (
							<div
								key={page.id}
								className={`sb-page-thumb ${idx === activePageIdx ? "sb-page-thumb--active" : ""}`}
							>
								<button
									className="sb-page-thumb__btn"
									onClick={() => switchToPage(idx)}
									title={`Página ${idx + 1}`}
								>
									{idx + 1}
								</button>
								{pages.length > 1 && (
									<button
										className="sb-page-thumb__delete"
										onClick={(e) => { e.stopPropagation(); deletePage(idx); }}
										title="Eliminar página"
									>
										×
									</button>
								)}
							</div>
						))}
						<button className="sb-page-add" onClick={addNewPage} title="Añadir página">
							<FiPlus size={16} />
						</button>
					</div>
				</main>
			</div>
		</div>
	);
}
